import type { Routine } from "@ark-bot/protocol";
import { useCallback, useEffect, useState } from "react";

import { api } from "../api.js";
import { formatLocale, useText } from "../i18n.js";
import { ROUTINES } from "../text/routines.js";
import { deviceTimeZone, useTimeZone } from "../time-zone.js";

import { useConfirm } from "./ConfirmDialog.js";

interface Props {
  readonly agentId: string;
  readonly agentName: string;
  /** Bumped when a `routines.changed` event arrives, so a run started elsewhere shows up here. */
  readonly revision: number;
  /** Open on a blank form: the pane's "Create routine" was pressed. */
  readonly startCreating?: boolean;
  /** Open on this routine's form: one was pressed in the details list. */
  readonly startEditing?: string | null;
  readonly onClose: () => void;
}

interface Draft {
  readonly id: string | null;
  readonly name: string;
  readonly prompt: string;
  readonly schedule: string;
}

const BLANK: Draft = { id: null, name: "", prompt: "", schedule: "0 9 * * 1-5" };

function when(ms: number | null, timeZone: string): string {
  if (ms == null) return "—";
  try {
    return new Intl.DateTimeFormat(formatLocale(), { timeZone, weekday: "short", day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }).format(new Date(ms));
  } catch {
    return new Date(ms).toLocaleString(formatLocale());
  }
}

function toDraft(routine: Routine): Draft {
  return { id: routine.id, name: routine.name, prompt: routine.prompt, schedule: routine.schedule };
}

/**
 * The agent's standing orders (ARCHITECTURE §6.9): what it will do, when, and
 * how the last few runs went. The agent writes these itself as well, so this
 * panel is a view of the same rows rather than the only way in. A row opens
 * the editor, which is also where run-now, pause and delete live.
 */
export function Routines({ agentId, agentName, revision, startCreating = false, startEditing = null, onClose }: Props): React.JSX.Element {
  const t = useText(ROUTINES);
  const [routines, setRoutines] = useState<Routine[] | null>(null);
  /** The zone schedules are read in, on the host; the zone the page shows the runs in may differ until the user picks one. */
  const [timeZone, setTimeZone] = useState<string>("UTC");
  const shown = useTimeZone();
  const [draft, setDraft] = useState<Draft | null>(startCreating ? BLANK : null);
  const [openedEdit, setOpenedEdit] = useState(startCreating);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    try {
      const list = await api.routines(agentId);
      setRoutines(list.routines);
      setTimeZone(list.timeZone);
      setError(null);
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
    }
  }, [agentId]);

  useEffect(() => {
    void load();
  }, [load, revision]);

  useEffect(() => {
    if (openedEdit || startCreating || startEditing == null || routines == null) return;
    const found = routines.find((routine) => routine.id === startEditing);
    if (found != null) setDraft(toDraft(found));
    setOpenedEdit(true);
  }, [routines, startEditing, startCreating, openedEdit]);

  /** Every write goes through here so one failure reports itself and the list still reloads. */
  const confirm = useConfirm();
  const act = async (work: () => Promise<unknown>): Promise<boolean> => {
    if (busy) return false;
    setBusy(true);
    setError(null);
    try {
      await work();
      await load();
      return true;
    } catch (failure) {
      setError(failure instanceof Error ? failure.message : String(failure));
      return false;
    } finally {
      setBusy(false);
    }
  };

  const save = async (event: React.FormEvent): Promise<void> => {
    event.preventDefault();
    if (draft == null) return;
    const body = { name: draft.name.trim(), prompt: draft.prompt.trim(), schedule: draft.schedule.trim() };
    const saved = await act(() => (draft.id == null ? api.createRoutine(agentId, body) : api.updateRoutine(agentId, draft.id, body)));
    if (saved) setDraft(null);
  };

  const remove = async (routine: Routine): Promise<void> => {
    const yes = await confirm({ title: t.deleteTitle(routine.name), message: t.deleteMessage, action: t.delete, danger: true });
    if (!yes) return;
    const deleted = await act(() => api.deleteRoutine(agentId, routine.id));
    if (deleted) setDraft(null);
  };

  const browserZone = deviceTimeZone();
  const current = draft?.id == null ? null : (routines ?? []).find((routine) => routine.id === draft.id) ?? null;
  const last = current?.runs[0];

  return (
    <section className="agent-settings routines">
      <div className="settings-head">
        <h2>{t.title}</h2>
        <span className="muted">
          {t.intro(agentName, timeZone)}
          {browserZone && browserZone !== timeZone ? (
            <>
              {" "}
              <button type="button" className="linkish" onClick={() => void act(() => shown.choose(browserZone))}>
                {t.useBrowserZone(browserZone)}
              </button>
            </>
          ) : null}
        </span>
        <button type="button" className="head-close" onClick={onClose} aria-label={t.close}>
          ×
        </button>
      </div>

      {error != null ? <div className="settings-error">{error}</div> : null}

      {draft != null ? (
        <form className="routine-form" onSubmit={(event) => void save(event)}>
          {/* An existing routine opens on where it stands, with the two things done to it without editing it: run it now, or pause it. */}
          {current == null ? null : (
            <div className={`routine-status${current.enabled ? "" : " paused"}`}>
              <div className="routine-line">
                <span className="muted">{current.enabled ? t.next(when(current.nextRunAt, shown.zone)) : t.paused}</span>
                <span className="routine-spacer" />
                {last == null ? null : <span className={`run-status run-${last.status}`}>{last.status === "running" ? t.running : t.last(last.status)}</span>}
              </div>
              {last?.detail == null ? null : <div className="muted routine-detail">{last.detail}</div>}
              <div className="routine-controls">
                <button type="button" disabled={busy} onClick={() => void act(() => api.runRoutine(agentId, current.id))}>
                  <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true">
                  <path d="M6.5 4.5v11l9-5.5-9-5.5Z" fill="currentColor" />
                </svg>
                  {t.runNow}
                </button>
                <button type="button" disabled={busy} onClick={() => void act(() => api.updateRoutine(agentId, current.id, { enabled: !current.enabled }))}>
                  {current.enabled ? (
                    <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true">
                    <path d="M7 4.5v11M13 4.5v11" fill="none" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" />
                  </svg>
                  ) : (
                    <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true">
                    <path d="M4 10a6 6 0 1 0 1.8-4.3M4 4.5v3.5h3.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                  )}
                  {current.enabled ? t.pause : t.resume}
                </button>
              </div>
            </div>
          )}
          <label>
            {t.name}
            <input value={draft.name} onChange={(event) => setDraft({ ...draft, name: event.target.value })} placeholder={t.namePlaceholder} required />
          </label>
          <label>
            {t.schedule} <span className="muted">{t.scheduleHint}</span>
            <input value={draft.schedule} onChange={(event) => setDraft({ ...draft, schedule: event.target.value })} placeholder="0 9 * * 1-5" required />
          </label>
          <label>
            {t.instruction} <span className="muted">{t.instructionHint}</span>
            <textarea rows={3} value={draft.prompt} onChange={(event) => setDraft({ ...draft, prompt: event.target.value })} placeholder={t.instructionPlaceholder} required />
          </label>
          {/* One footer, as a dialog's: deleting apart on the left, keeping or dropping the edit on the right. */}
          <div className="routine-footer">
            {current == null ? null : (
              <button type="button" className="routine-delete" disabled={busy} onClick={() => void remove(current)}>
                {t.delete}
              </button>
            )}
            <span className="routine-spacer" />
            <button type="button" className="quiet" onClick={() => setDraft(null)} disabled={busy}>
              {t.cancel}
            </button>
            <button type="submit" className="primary" disabled={busy}>
              {draft.id == null ? t.create : t.save}
            </button>
          </div>
        </form>
      ) : routines == null ? (
        <div className="muted">{t.loading}</div>
      ) : routines.length === 0 ? (
        <div className="muted">{t.empty(agentName)}</div>
      ) : (
        <ul className="routine-list">
          {routines.map((routine) => {
            const run = routine.runs[0];
            return (
              <li key={routine.id}>
                <button type="button" className={routine.enabled ? "routine routine-open" : "routine routine-open paused"} onClick={() => setDraft(toDraft(routine))} aria-label={t.open(routine.name)}>
                  <div className="routine-line">
                    <strong>{routine.name}</strong>
                    <span className="muted">{routine.scheduleDescription}</span>
                    <span className="routine-spacer" />
                    <span className="muted">{routine.enabled ? t.next(when(routine.nextRunAt, shown.zone)) : t.paused}</span>
                    {run == null ? null : <span className={`run-status run-${run.status}`}>{run.status === "running" ? t.running : t.last(run.status)}</span>}
                  </div>
                  <div className="routine-prompt">{routine.prompt}</div>
                  {run?.detail == null ? null : <div className="muted routine-detail">{run.detail}</div>}
                </button>
              </li>
            );
          })}
        </ul>
      )}

      {draft == null ? (
        <div className="settings-actions">
          <button type="button" onClick={() => setDraft(BLANK)} disabled={routines == null}>
            {t.newRoutine}
          </button>
        </div>
      ) : null}
    </section>
  );
}
